import type { TimeEntryDto } from '@/features/time-tracking/public';
import { useUIStore } from '@/core/stores/ui.store';
import { formatInstant } from '@/shared/lib/datetime/instant';

interface ExportOptions {
  /** Localized column headers, in column order. */
  headers: string[];
  filename: string;
  billableLabels: { yes: string; no: string };
}

/** UTF-8 BOM so spreadsheet apps (Excel) render accents correctly. */
const UTF8_BOM = String.fromCharCode(0xfeff);

function moment(iso: string | null): string {
  const { dateFormat, timeFormat, timezone } = useUIStore.getState();
  return formatInstant(iso, { dateFormat, timeFormat, timezone, showTime: true });
}

/** Escapes a CSV cell (wraps in quotes when it contains separators/quotes). */
function escapeCell(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/**
 * Generates a CSV of the given time entries and triggers a client-side download.
 *
 * @remarks No server-side export endpoint exists; this exports the current
 * result set from the browser.
 */
export function exportTimeEntriesCsv(entries: TimeEntryDto[], options: ExportOptions): void {
  const { headers, filename, billableLabels } = options;

  const rows = entries.map((e) => [
    e.task?.title ?? '',
    e.task?.project?.name ?? '',
    e.user?.displayName ?? '',
    moment(e.startTime),
    moment(e.endTime),
    String(e.durationHours ?? 0),
    e.isBillable ? billableLabels.yes : billableLabels.no,
  ]);

  const csv = [headers, ...rows]
    .map((row) => row.map((cell) => escapeCell(String(cell))).join(','))
    .join('\n');

  const blob = new Blob([UTF8_BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
